import { isBefore, parseISO } from "date-fns";
import type React from "react";
import { useMemo } from "react";

import type { SurveyDetailsV2Dto } from "@/api/types";
import { CountdownLabel } from "@/components/CountdownLabel/CountdownLabel";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { CalendarCheck01Icon } from "@/components/Icons/CalendarCheck01Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { ClockFastForwardIcon } from "@/components/Icons/ClockFastForwardIcon";
import { Label } from "@/components/Label/Label";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useTranslation } from "@/translations";

import { SurveyAuthor } from "./SurveyAuthor";

interface SurveyInfoSectionProps {
  survey: SurveyDetailsV2Dto;
  showStatus?: boolean;
  showDuration?: boolean;
  showCountdown?: boolean;
}

export function SurveyInfoSection({
  survey,
  showStatus,
  showDuration,
  showCountdown,
}: SurveyInfoSectionProps): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();

  const { startDate, endDate, isRunning, isFinished, isUpcoming } = useMemo(() => {
    const currStartDate = survey.startsAt ? parseISO(survey.startsAt) : undefined;
    const currEndDate = survey.endsAt ? parseISO(survey.endsAt) : undefined;

    return {
      startDate: currStartDate,
      endDate: currEndDate,
      isRunning:
        currStartDate && currEndDate && isBefore(currStartDate, new Date()) && isBefore(new Date(), currEndDate),
      isUpcoming: currStartDate && isBefore(new Date(), currStartDate),
      isFinished: !!currEndDate && isBefore(currEndDate, new Date()),
    };
  }, [survey]);

  return (
    <div className="flex w-full flex-col gap-6 rounded-lg bg-white p-4">
      {/* Special label */}
      {((survey.isInternal && sessionUser.isSuperAdmin) || showCountdown) && (
        <div className="flex flex-wrap gap-1">
          {survey.isInternal && sessionUser.isSuperAdmin && (
            <Label theme="blue">{t("page.survey-details.info.internal")}</Label>
          )}
          {showCountdown && survey.endsAt && <CountdownLabel endDate={new Date(survey.endsAt)} />}
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h2 className="text-headline2">{survey.title}</h2>
        <div className="flex items-center gap-1">
          {survey.createdBy && <SurveyAuthor author={survey.createdBy} />}
          <p className="italic">
            {t("page.survey-details.info.group.in", {
              groupName: survey.group ? survey.group.name : t("page.survey-details.info.group.general"),
            })}
          </p>
        </div>
      </div>

      <p className="whitespace-pre-wrap">{survey.description}</p>

      {(showDuration || showStatus) && (
        <>
          <hr className="text-grey-300" />

          <div className="flex flex-wrap items-center gap-4">
            {showStatus && (
              <div className="flex min-w-[280px] items-center gap-2">
                {isFinished && (
                  <Label icon={<CheckIcon />} size="lg" theme="blue">
                    {t("page.survey-details.info.finished")}
                  </Label>
                )}
                {isRunning && (
                  <Label icon={<CalendarCheck01Icon />} size="lg" theme="green">
                    {t("page.survey-details.info.running")}
                  </Label>
                )}
                {isUpcoming && (
                  <Label icon={<ClockFastForwardIcon />} size="lg" theme="yellow">
                    {t("page.survey-details.info.upcoming")}
                  </Label>
                )}
                {(isFinished || isRunning) && (
                  <strong>({t("page.survey-details.info.responses", { count: survey.answerCount || 0 })})</strong>
                )}
              </div>
            )}

            {showDuration && (
              <table>
                <tbody>
                  {startDate && (
                    <tr>
                      <td className="whitespace-nowrap pr-1">
                        {isUpcoming
                          ? t("page.survey-details.info.starts-at")
                          : t("page.survey-details.info.started-at")}
                        :{" "}
                      </td>
                      <td className="w-full text-body-bold">
                        <FormattedDate date={startDate} format="datetime" />
                      </td>
                    </tr>
                  )}
                  {endDate && (
                    <tr>
                      <td className="whitespace-nowrap pr-1">
                        {isUpcoming || isRunning
                          ? t("page.survey-details.info.ends-at")
                          : t("page.survey-details.info.ended-at")}
                        :{" "}
                      </td>
                      <td className="w-full text-body-bold">
                        <FormattedDate date={endDate} format="datetime" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
